import { useEffect } from "react";

/**
 * SeoHead — client-side meta manager for React public routes.
 *
 * IMPORTANT: /app/frontend/public/index.html declares `<meta name="robots" content="noindex, nofollow">`
 * as the safe default for the React SPA shell. On PUBLIC routes we override that
 * to `index, follow`. Private routes leave the noindex in place.
 *
 * For real search-engine indexing we rely on the FastAPI Jinja2 renderer in
 * backend/routes/public_seo.py — bots that do not execute JS get the same HTML
 * with metadata pre-rendered. This component is progressive enhancement for
 * renderers (like Googlebot's second pass) that do run JS.
 */
export default function SeoHead({
  title,
  description,
  canonicalPath = "",
  ogType = "website",
  robots = "index, follow, max-snippet:-1, max-image-preview:large",
  hreflang = null,
  jsonLd = null,
}) {
  useEffect(() => {
    const publicBase = (process.env.REACT_APP_PUBLIC_BASE_URL || "https://throughlinehousing.com").replace(/\/$/, "");
    const canonical = canonicalPath ? `${publicBase}${canonicalPath}` : publicBase + window.location.pathname;

    if (title) document.title = title;

    const setMeta = (attr, name, content) => {
      if (content == null) return;
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta("name", "description", description);
    setMeta("name", "robots", robots);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:type", ogType);
    setMeta("property", "og:url", canonical);
    setMeta("property", "og:site_name", "Throughline");

    // canonical
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement("link"); link.setAttribute("rel", "canonical"); document.head.appendChild(link); }
    link.setAttribute("href", canonical);

    // hreflang alternates — clear then re-add
    document.head.querySelectorAll('link[rel="alternate"][data-seo="hreflang"]').forEach(n => n.remove());
    (hreflang || []).forEach(({ lang, path }) => {
      const l = document.createElement("link");
      l.setAttribute("rel", "alternate");
      l.setAttribute("hreflang", lang);
      l.setAttribute("href", `${publicBase}${path}`);
      l.setAttribute("data-seo", "hreflang");
      document.head.appendChild(l);
    });

    // JSON-LD
    document.head.querySelectorAll('script[type="application/ld+json"][data-seo="jsonld"]').forEach(n => n.remove());
    if (jsonLd) {
      const s = document.createElement("script");
      s.setAttribute("type", "application/ld+json");
      s.setAttribute("data-seo", "jsonld");
      s.textContent = typeof jsonLd === "string" ? jsonLd : JSON.stringify(jsonLd);
      document.head.appendChild(s);
    }
  }, [title, description, canonicalPath, ogType, robots, JSON.stringify(hreflang || []), JSON.stringify(jsonLd || null)]);

  return null;
}
